export { findBlock } from '../../core/tree-read'
import type { Block } from '@w/content-blocks'
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { locate, rows } from '../../core/tree-read'
import type { CoreViewApi } from '../view-api'
import { renderedBlock } from '../dom-selection'

export type OverlayApi = CoreViewApi

export function select(api: OverlayApi, selection: CoreViewApi['state']['selection']) {
  api.select(selection)
}

export function blockElement(api: CoreViewApi, id: string) {
  return (api.root && renderedBlock(api.root, id)) ?? null
}

export function topBlock(api: CoreViewApi, target: EventTarget | null): Block | undefined {
  let element = target instanceof Element ? target : null
  while (element && element !== api.root) {
    const id = element instanceof HTMLElement ? element.dataset.blockId : null
    const block = api.state.blocks.find(item => item.id === id)
    if (block) return block
    element = element.parentElement
  }
  return undefined
}

export function focusedBlock(api: CoreViewApi) {
  const selection = api.state.selection
  if (!selection) return null
  // A text selection names no node: no scan of the top-level blocks (one per key and overlay render).
  const node = selection.nodes?.[0]
  const selected = node === undefined ? undefined : api.state.blocks.find(block => block.id === node)
  if (selected) return selected
  const entries = rows(api.state.blocks)
  const row = entries[locate(entries, selection.focus).row]
  return row?.parents[0] ?? row?.block
}


export const segment = (value: string) => value.toLowerCase().replaceAll(/[^a-z0-9]+/gu, '-').replaceAll(/^-|-$/gu, '')
export const panelClass = 'border-border bg-surface-2 text-on-surface z-[1000] rounded-xl border p-1.5 shadow-xl'
export const itemClass = 'text-on-surface hover:bg-primary/8 aria-selected:bg-primary/8 flex min-h-9 w-full items-center gap-2 rounded-md px-2 text-left text-sm font-normal disabled:opacity-50'

export function OverlayPortal({ api, children }: { api: CoreViewApi; children: ReactNode }) {
  return api.root ? createPortal(<div data-core-overlay="true" contentEditable={false}>{children}</div>, api.root.ownerDocument.body) : null
}

/** Fixed coordinates track scrolling, viewport resize and selection changes. */
export function usePosition(api: CoreViewApi, target?: HTMLElement | null, above = false, panel?: HTMLElement | null, { center = above, gap = 8, triggerLength = 0, anchorRect }: { center?: boolean; gap?: number; triggerLength?: number; anchorRect?: DOMRect } = {}) {
  const [position, setPosition] = useState<CSSProperties>({ position: 'fixed', left: 8, top: 8 })
  useLayoutEffect(() => {
    const root = api.root
    if (!root || !panel) return () => {}
    const update = () => {
      const selection = root.ownerDocument.getSelection()
      const range = api.state.selection && selection?.rangeCount ? selection.getRangeAt(0).cloneRange() : null
      if (range && triggerLength && range.startContainer.nodeType === Node.TEXT_NODE) {
        range.setStart(range.startContainer, Math.max(0, range.startOffset - triggerLength))
      }
      const bounds = target?.getBoundingClientRect() ?? anchorRect ?? range?.getBoundingClientRect() ?? root.getBoundingClientRect()
      const viewport = window.visualViewport
      const width = viewport?.width ?? window.innerWidth
      const height = viewport?.height ?? window.innerHeight
      const x = viewport?.offsetLeft ?? 0, y = viewport?.offsetTop ?? 0
      const size = panel?.getBoundingClientRect()
      const panelWidth = Math.min(size?.width ?? 280, width - 16)
      const panelHeight = Math.min(size?.height ?? 44, height - 16)
      const desired = above || bounds.bottom + gap + panelHeight > y + height - 8 ? bounds.top - panelHeight - gap : bounds.bottom + gap
      const next: CSSProperties = { position: 'fixed', left: Math.max(x + 8, Math.min(bounds.left + (center ? bounds.width / 2 - panelWidth / 2 : 0), x + width - panelWidth - 8)),
        top: Math.max(y + 8, Math.min(desired, y + height - panelHeight - 8)), maxWidth: width - 16, maxHeight: Math.min(320, height - 16) }
      setPosition(current => Object.keys(next).every(key => current[key as keyof CSSProperties] === next[key as keyof CSSProperties]) ? current : next)
    }
    update()
    window.addEventListener('resize', update)
    document.addEventListener('scroll', update, true)
    window.visualViewport?.addEventListener('resize', update)
    window.visualViewport?.addEventListener('scroll', update)
    const observer = new ResizeObserver(update)
    if (panel) observer.observe(panel)
    return () => {
      window.removeEventListener('resize', update)
      document.removeEventListener('scroll', update, true)
      window.visualViewport?.removeEventListener('resize', update)
      window.visualViewport?.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [api.root, api.state.selection, target, above, panel, center, gap, triggerLength, anchorRect])
  return position
}

/** Event listeners read the last committed API, including synchronous selection writes by the host. */
export function useLiveApi(api: OverlayApi) {
  const current = useRef(api)
  useLayoutEffect(() => { current.current = api })
  return current
}

export function useDismiss(open: boolean, close: () => void, container: { current: HTMLElement | null }) {
  useEffect(() => {
    if (!open) return () => {}
    const pointer = (event: PointerEvent) => {
      if (event.target instanceof Node && !container.current?.contains(event.target)) close()
    }
    const key = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopImmediatePropagation()
      close()
    }
    document.addEventListener('pointerdown', pointer)
    document.addEventListener('keydown', key, true)
    return () => {
      document.removeEventListener('pointerdown', pointer)
      document.removeEventListener('keydown', key, true)
    }
  }, [open, close, container])
}

/** Arrow navigation supplements native button Tab/Enter behavior in compact action menus. */
export function menuKey(event: ReactKeyboardEvent<HTMLElement>) {
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return
  const items = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')]
  if (items.length === 0) return
  event.preventDefault()
  event.stopPropagation()
  const index = items.findIndex(item => item === event.currentTarget.ownerDocument.activeElement)
  const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
    : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length
  items[next].focus()
}
